import React, { useEffect, useId, useState } from 'react';
import DsSelectBase from '../../common/DsSelect';
import DsButton from '../../common/DsButton';
import '../../../css/common/CastingReference.css';
import { getEventCastingContext, type CastingContext, type CastingContextInput } from '../../../api/castingReference';
import CastingPositionEditor from './CastingPositionEditor';
const DsSelect = DsSelectBase as React.ComponentType<React.SelectHTMLAttributes<HTMLSelectElement>>;

type Genre = { id?: number; label?: string; casting_axis_profile?: string | null };
type Props = { genres: Genre[]; selectedGenreIds: (string | number)[]; eventId?: string;
  value?: string; saveHint?: string; disabled?: boolean; onChange: (value: string) => void };
export default function CastingContextFields({ genres, selectedGenreIds, eventId, value, onChange, saveHint, disabled = false }: Props) {
  const id = useId();
  const [saved, setSaved] = useState<CastingContext | null>(null);
  const [loading, setLoading] = useState(Boolean(eventId));
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!eventId) { setSaved(null); setLoading(false); return; }
    const controller = new AbortController();
    setSaved(null); setLoading(true); setError('');
    getEventCastingContext(eventId, controller.signal).then(data => {
      if (!controller.signal.aborted) setSaved(data.context);
    }).catch(err => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [eventId, retry]);
  const eligible = genres.filter(g => g.casting_axis_profile === 'orchestra_v1' && selectedGenreIds.map(Number).includes(Number(g.id)));
  if (!eligible.length) return null;
  let draft: CastingContextInput | undefined;
  try { if (value) draft = JSON.parse(value); } catch { /* A restored invalid draft is not used. */ }
  const active = draft || saved;
  const genreId = active && eligible.some(g => g.id === active.genreId) ? active.genreId
    : eligible.length === 1 ? eligible[0].id : undefined;
  const applicable = active?.genreId === genreId;
  const change = (patch: Partial<CastingContextInput>) => {
    if (disabled || loading || error || (!genreId && !patch.genreId)) return;
    const input: CastingContextInput = { expectedRevision: saved?.revision || 0, genreId: genreId || patch.genreId!,
      profileKey: 'orchestra_v1', difficultyRank: applicable ? active?.difficultyRank as CastingContextInput['difficultyRank'] ?? null : null,
      musicalStyle: applicable ? active?.musicalStyle as CastingContextInput['musicalStyle'] ?? null : null,
      position: applicable ? active?.position ?? null : null,
      rationale: applicable ? active?.rationale || '' : '', ...patch };
    onChange(JSON.stringify(input));
  };
  return <fieldset className="form-field form-field--full casting-context-fields">
    <legend>キャスティングの参考情報</legend>
    <p className="form-hint">演奏内容の難しさと音楽的傾向を設定します。リハーサル・拘束・移動条件は含めません。</p>
    {loading && <p role="status">分析設定を読み込んでいます…</p>}
    {error && <p role="alert">{error}</p>}
    {error && <DsButton disabled={disabled} variant="secondary" onClick={() => setRetry(v => v + 1)}>再読み込み</DsButton>}
    {eligible.length > 1 && <label>評価するジャンル<DsSelect value={genreId || ''} disabled={disabled || loading || !!error} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => change({ genreId: Number(e.target.value), difficultyRank: null, musicalStyle: null, position: null, rationale: '' })}>
      <option value="">選択してください</option>{eligible.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
    </DsSelect></label>}
    <CastingPositionEditor genreId={genreId} eventId={eventId} value={applicable ? active?.position : null} disabled={disabled || !genreId || loading || !!error}
      onChange={position => change({ position, difficultyRank: null, musicalStyle: null })} />
    {applicable && !active?.position && (active?.difficultyRank || active?.musicalStyle) && <p className="form-hint">以前の段階式設定：難易度 {active?.difficultyRank || '未設定'} ／ 演奏傾向 {active?.musicalStyle || '未設定'}。位置は未配置です。</p>}
    <label htmlFor={`${id}-reason`}>設定の根拠（任意・管理者向け）</label>
    <textarea id={`${id}-reason`} maxLength={500} value={applicable ? active?.rationale || '' : ''}
      onChange={e => change({ rationale: e.target.value })} disabled={disabled || !genreId || loading || !!error} />
    <p className="form-hint">{saveHint || 'イベントを保存すると反映されます。公演全体の設定であり、個人の実力評価ではありません。'}</p>
  </fieldset>;
}
